"use client";

import { useState, ReactNode } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

interface ElementCarouselProps {
    elements: ReactNode[];
    className?: string;
    showDots?: boolean;
    showArrows?: boolean;
}

export function ElementCarousel({ 
    elements, 
    className = "",
    showDots = true,
    showArrows = true,
}: ElementCarouselProps) {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (elements.length === 0) {
        return null;
    }

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? elements.length - 1 : prev - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev === elements.length - 1 ? 0 : prev + 1));
    };

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    return (
        <div className={`relative flex flex-col gap-y-3 lg:gap-y-1 items-center justify-center w-full h-full ${className}`}>
            {/* Main Content Container */}
            <div className="relative flex items-center justify-center w-full h-[calc(100%-3rem)]">
                {/* Left Arrow */}
                {showArrows && (
                    <button
                        onClick={goToPrevious}
                        className="absolute left-2 z-10 flex items-center justify-center w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200 cursor-pointer"
                        aria-label="Previous element"
                    >
                        <LeftOutlined className="text-lg" />
                    </button>
                )}

                {/* Content */}
                <div className="flex items-center justify-center w-full h-full overflow-hidden">
                    {elements[currentIndex]}
                </div>

                {/* Right Arrow */}
                {showArrows && (
                    <button
                        onClick={goToNext}
                        className="absolute right-2 z-10 flex items-center justify-center w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-200 cursor-pointer"
                        aria-label="Next element"
                    >
                        <RightOutlined className="text-lg" />
                    </button>
                )}
            </div>

            {/* Dots Indicator */}
            {showDots && (
                <div className="flex items-center justify-center gap-2 h-12 shrink-0 flex-wrap">
                    {elements.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all duration-200 cursor-pointer ${
                                index === currentIndex
                                    ? "bg-gyellow scale-110"
                                    : "bg-gray-400 hover:bg-gray-300"
                            }`}
                            aria-label={`Go to element ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
